import React from 'react';
import { CellProps, Renderer } from 'react-table';
import { QBadge } from '../../QAtoms';
import { QDataColumn, QDataRow, QDataItem } from '../../QAtoms/Table';
import { CellRenderProps } from './CellRenderProps';
import { TextCell } from './Cells/Text';

export type DataView<T> = {
  [K in keyof T]?: {
    /**
     * The text to display in the header of the column.
     */
    header: string;
    /**
     * A custom renderer for the cell.
     * @param value The value of the cell.
     * @param original The original item, for convenience in more complex use cases.
     */
    render?: (value: T[K], original: Readonly<T>) => React.ReactNode;
  } & Pick<QDataColumn, 'width'>;
};

export type ExampleViewData = {
  fullName: string;
  email: string;
  groups: readonly string[];
};

export const ExampleView: DataView<ExampleViewData> = {
  fullName: {
    header: 'Full name',
  },
  email: {
    header: 'Email',
  },
  groups: {
    header: 'Groups',
    render: (groups) =>
      groups.map((g) => (
        <QBadge mx="2px" key={g}>
          {g}
        </QBadge>
      )),
  },
};

const keysOf = <T,>(view: DataView<T>) => Object.keys(view) as (keyof T)[];

/**
 * Convert a DataView into a list of QDataColumns.
 */
export const toColumns = <T,>(view: DataView<T>): QDataColumn[] =>
  keysOf(view)
    .map((key): QDataColumn | null => {
      const definition = view[key];
      if (!definition) {
        return null;
      }
      const { header, render, width } = definition;
      return {
        id: String(key),
        accessor: String(key),
        Header: header,
        width,
        // Type assertion: CellRenderProps<T> is structurally compatible with CellProps<QDataRow, QDataItem>
        // at runtime. We only destructure the properties we need (row.original, value), allowing us to
        // maintain a simplified API while satisfying react-table's type requirements.
        Cell: (render
          ? ({ row: { original } }: CellRenderProps<T>) =>
              render(original[key], original)
          : TextCell) as unknown as Renderer<CellProps<QDataRow, QDataItem>>,
        disableSortBy: true,
      };
    })
    .filter(Boolean) as QDataColumn[];
